import { collection, getDocs, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../firebase';
import AdvancedRolesService from './advancedRolesService';

/**
 * Service pour gérer l'initialisation de l'application multi-instance
 * Gère la création du premier super administrateur lorsque la base est vide
 * Utilise un document système public pour contourner les problèmes de permissions
 */
class InitializationService {
  /**
   * Vérifie si la base de données contient des utilisateurs
   * @returns {Promise<boolean>} true si la base est vide, false sinon
   */
  static async isDatabaseEmpty() {
    try {
      const employeesSnapshot = await getDocs(collection(db, 'employees'));
      const isEmpty = employeesSnapshot.empty;
      console.log('🔍 Vérification base vide:', isEmpty, `(${employeesSnapshot.size} documents)`);
      return isEmpty;
    } catch (error) {
      console.error('❌ Erreur lors de la vérification de la base:', error.code, error.message);
      // Si l'erreur est due aux permissions (permission-denied), 
      // on suppose que la base n'est pas vide (car des règles de sécurité existent)
      if (error.code === 'permission-denied') {
        console.log('⚠️ Accès refusé - La base contient probablement des utilisateurs');
        return false;
      }
      // Pour toute autre erreur, on suppose que la base n'est pas vide par sécurité
      return false;
    }
  }

  /**
   * Initialise les rôles avancés si nécessaire
   * @returns {Promise<boolean>} true si les rôles ont été initialisés
   */
  static async initializeRolesIfNeeded() {
    try {
      const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
      
      if (rolesSnapshot.empty) {
        console.log('🔧 Initialisation des rôles avancés...');
        await AdvancedRolesService.initializeAdvancedRoles('system');
        console.log('✅ Rôles avancés initialisés avec succès');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation des rôles:', error);
      throw error;
    }
  }

  /**
   * Crée le premier super administrateur
   * @param {Object} userData - Données de l'utilisateur
   * @param {string} userData.email - Email du super admin
   * @param {string} userData.password - Mot de passe
   * @param {string} userData.firstName - Prénom
   * @param {string} userData.lastName - Nom
   * @param {string} userData.phone - Téléphone (optionnel)
   * @returns {Promise<Object>} Résultat de la création avec l'UID de l'utilisateur
   */
  static async createFirstSuperAdmin(userData) {
    try {
      const { email, password, firstName, lastName, phone } = userData;

      // Vérifier que l'initialisation n'a pas déjà été faite via le document système
      try {
        const systemDoc = await getDoc(doc(db, 'system', 'initialization'));
        if (systemDoc.exists() && systemDoc.data().isInitialized === true) {
          throw new Error('L\'application a déjà été initialisée');
        }
      } catch (error) {
        if (error.message === 'L\'application a déjà été initialisée') {
          throw error;
        }
        // Si erreur de lecture, on continue (peut-être que le doc n'existe pas encore)
        console.log('⚠️ Impossible de vérifier le statut, on continue...');
      }

      // Créer d'abord l'utilisateur dans Firebase Auth (cela l'authentifie automatiquement)
      console.log('🔧 Création du compte Firebase Auth...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;
      console.log('✅ Utilisateur Firebase Auth créé et authentifié');

      // Maintenant que l'utilisateur est authentifié, initialiser les rôles si nécessaire
      await this.initializeRolesIfNeeded();

      // Créer le document employee avec le rôle super_admin
      console.log('🔧 Création du document employee...');
      const employeeData = {
        userId: userId,
        email: email,
        firstName: firstName,
        lastName: lastName,
        phone: phone || '',
        role: 'super_admin', // Rôle super administrateur
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        position: 'Super Administrateur',
        color: '#9333EA', // Couleur violette pour le super admin
        permissions: {
          // Permissions complètes pour toutes les ressources
          appointments: { read: true, write: true, create: true, delete: true, manage: true },
          clients: { read: true, write: true, create: true, delete: true, manage: true },
          contracts: { read: true, write: true, create: true, delete: true, manage: true },
          employees: { read: true, write: true, create: true, delete: true, manage: true },
          positions: { read: true, write: true, create: true, delete: true, manage: true },
          roles: { read: true, write: true, create: true, delete: true, manage: true },
          settings: { read: true, write: true, create: true, delete: true, manage: true },
          taskCategories: { read: true, write: true, create: true, delete: true, manage: true },
          tasks: { read: true, write: true, create: true, delete: true, manage: true }
        }
      };

      await setDoc(doc(db, 'employees', userId), employeeData);

      // Marquer l'application comme initialisée
      await this.markAsInitialized();

      console.log('✅ Super administrateur créé avec succès!');
      console.log(`📧 Email: ${email}`);
      console.log(`👤 Nom: ${firstName} ${lastName}`);
      console.log(`🔑 UID: ${userId}`);

      return {
        success: true,
        userId: userId,
        email: email,
        role: 'super_admin',
        message: 'Premier super administrateur créé avec succès'
      };

    } catch (error) {
      console.error('❌ Erreur lors de la création du super admin:', error);
      
      // Nettoyer en cas d'erreur
      if (auth.currentUser) {
        try {
          await auth.currentUser.delete();
        } catch (cleanupError) {
          console.error('Erreur lors du nettoyage:', cleanupError);
        }
      }

      throw {
        success: false,
        error: error.message || 'Erreur lors de la création du super administrateur',
        details: error
      };
    }
  }

  /**
   * Vérifie l'état de l'initialisation en utilisant un document système public
   * @returns {Promise<Object>} État de l'initialisation
   */
  static async getInitializationStatus() {
    try {
      // Vérifier d'abord le document système (doit être lisible publiquement)
      const systemDoc = await getDoc(doc(db, 'system', 'initialization'));
      
      if (systemDoc.exists()) {
        const data = systemDoc.data();
        console.log('📄 Document système trouvé:', data);
        return {
          needsInitialization: data.isInitialized === false,
          hasUsers: data.isInitialized === true,
          hasRoles: data.hasRoles || false,
          rolesCount: data.rolesCount || 0
        };
      }
      
      // Si le document système n'existe pas, vérifier si l'application est déjà initialisée
      // en regardant les autres collections
      console.log('📄 Document système non trouvé - Vérification des données existantes...');
      
      try {
        // Vérifier si des employés existent
        const employeesSnapshot = await getDocs(collection(db, 'employees'));
        const hasEmployees = !employeesSnapshot.empty;
        
        // Vérifier si des rôles existent
        const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
        const hasRoles = !rolesSnapshot.empty;
        
        console.log('🔍 Vérification données existantes:', {
          hasEmployees,
          employeesCount: employeesSnapshot.size,
          hasRoles,
          rolesCount: rolesSnapshot.size
        });
        
        // Si des employés ou rôles existent, l'application est déjà initialisée
        const isAlreadyInitialized = hasEmployees || hasRoles;
        
        if (isAlreadyInitialized) {
          console.log('✅ Application déjà initialisée (données existantes)');
          // Créer le document système pour refléter l'état actuel
          await setDoc(doc(db, 'system', 'initialization'), {
            isInitialized: true,
            hasRoles: hasRoles,
            rolesCount: rolesSnapshot.size,
            employeesCount: employeesSnapshot.size,
            initializedAt: serverTimestamp(),
            detectedAt: serverTimestamp()
          });
          
          return {
            needsInitialization: false,
            hasUsers: true,
            hasRoles: hasRoles,
            rolesCount: rolesSnapshot.size
          };
        } else {
          console.log('🔧 Initialisation nécessaire (base vide)');
          // Créer le document système pour l'initialisation
          await setDoc(doc(db, 'system', 'initialization'), {
            isInitialized: false,
            hasRoles: false,
            rolesCount: 0,
            createdAt: serverTimestamp()
          });
          
          return {
            needsInitialization: true,
            hasUsers: false,
            hasRoles: false,
            rolesCount: 0
          };
        }
        
      } catch (checkError) {
        console.log('⚠️ Erreur lors de la vérification des données:', checkError.message);
        // En cas d'erreur, supposer que l'initialisation est nécessaire
        await setDoc(doc(db, 'system', 'initialization'), {
          isInitialized: false,
          hasRoles: false,
          rolesCount: 0,
          createdAt: serverTimestamp()
        });
        
        return {
          needsInitialization: true,
          hasUsers: false,
          hasRoles: false,
          rolesCount: 0
        };
      }
      
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du statut:', error);
      // En cas d'erreur, supposer que l'initialisation est nécessaire par sécurité
      return {
        needsInitialization: true,
        hasUsers: false,
        hasRoles: false,
        rolesCount: 0
      };
    }
  }

  /**
   * Met à jour le document système après l'initialisation
   * @returns {Promise<void>}
   */
  static async markAsInitialized() {
    try {
      const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
      await setDoc(doc(db, 'system', 'initialization'), {
        isInitialized: true,
        hasRoles: !rolesSnapshot.empty,
        rolesCount: rolesSnapshot.size,
        initializedAt: serverTimestamp()
      });
      console.log('✅ Document système mis à jour - Application marquée comme initialisée');
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du document système:', error);
    }
  }
}

export default InitializationService;
